import React, { useState } from 'react';

import { Switch, Form, Divider, Select, InputNumber, Space } from 'antd';

import { getEnumList } from '@extensions/utils';

import { IUserSettings } from '@entities/user-settings';

import { UserType } from '@enums/user-type';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { IEnumItem } from '@enums/enum-item';

import { ExchangeRateCommissionType } from '@enums/exchange-rate-commission-type';

interface IUserSettingsForm {
    showCurrencyOperations: boolean;
    userType: UserType | undefined;
    userSettings: IUserSettings | undefined;
    isViewOnly: boolean;
    onChange: (e: IUserSettings) => void;
}

const UserSettingsForm = (props: IUserSettingsForm) => {
    const { showCurrencyOperations, userType, userSettings, isViewOnly, onChange } = props;

    const [rateCommissionTypes] = useState<Array<IEnumItem>>(getEnumList(ExchangeRateCommissionType));

    if (!userSettings) {
        return <>Настройки пользователя не иницированы!</>;
    }

    return (
        <>
            {showCurrencyOperations && (
                <>
                    <Form.Item wrapperCol={{ span: 24 }}>
                        <Divider titlePlacement='left' style={{ marginBottom: 0, marginTop: 0 }}>
                            Операции с валютой
                        </Divider>
                    </Form.Item>
                    <Form.Item
                        labelAlign='left'
                        label='Использовать курс ЦБ'
                        initialValue={userSettings?.useCBExchangeRate}
                        name='useCBExchangeRate'
                        style={{ marginBottom: 0 }}
                        labelCol={{ xs: 22 }}
                        wrapperCol={{ xs: 2 }}
                    >
                        <Switch
                            size='small'
                            style={{ float: 'right' }}
                            disabled={isViewOnly}
                            checked={userSettings?.useCBExchangeRate}
                            onChange={(value) => {
                                onChange({ ...userSettings, useInternalExchangeRate: false, useCBExchangeRate: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        labelAlign='left'
                        label='Использовать внутренний курс'
                        initialValue={userSettings?.useInternalExchangeRate}
                        name='useInternalExchangeRate'
                        labelCol={{ xs: 22 }}
                        wrapperCol={{ xs: 2 }}
                    >
                        <Switch
                            size='small'
                            style={{ float: 'right' }}
                            disabled={isViewOnly}
                            checked={userSettings?.useInternalExchangeRate}
                            onChange={(value) => {
                                onChange({ ...userSettings, useCBExchangeRate: false, useInternalExchangeRate: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item labelAlign='left' label='Комиссия за конвертацию валюты' labelCol={{ xs: 17 }} wrapperCol={{ xs: 7 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item noStyle initialValue={userSettings?.exchangeRateCommission} name='exchangeRateCommission'>
                                <InputNumber
                                    disabled={isViewOnly}
                                    precision={2}
                                    decimalSeparator=','
                                    min={0}
                                    onChange={(value: number | null) => {
                                        onChange({ ...userSettings, exchangeRateCommission: value });
                                    }}
                                />
                            </Form.Item>
                            <Select
                                style={{ width: '40%' }}
                                defaultValue={userSettings?.exchangeRateCommissionType || ExchangeRateCommissionType.Percentage}
                                disabled={isViewOnly}
                                onChange={(value: ExchangeRateCommissionType) => {
                                    onChange({ ...userSettings, exchangeRateCommissionType: value });
                                }}
                                options={rateCommissionTypes.map((c) => {
                                    return {
                                        value: c.value,
                                        label: c.value == ExchangeRateCommissionType.Percentage ? '%' : currencySign(Currency.Rub),
                                    };
                                })}
                            />
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item labelAlign='left' label='Комиссия за безналичный перевод' labelCol={{ xs: 17 }} wrapperCol={{ xs: 7 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item noStyle initialValue={userSettings?.nonCashTransferCommission} name='nonCashTransferCommission'>
                                <InputNumber
                                    disabled={isViewOnly}
                                    style={{ width: '100%' }}
                                    precision={2}
                                    decimalSeparator=','
                                    min={0}
                                    onChange={(value: number | null) => {
                                        onChange({ ...userSettings, nonCashTransferCommission: value });
                                    }}
                                />
                            </Form.Item>
                            <Space.Addon>%</Space.Addon>
                        </Space.Compact>
                    </Form.Item>

                     <Form.Item labelAlign='left' label='Комиссия за наличный перевод' labelCol={{ xs: 17 }} wrapperCol={{ xs: 7 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item noStyle initialValue={userSettings?.cashTransferCommission} name='cashTransferCommission'>
                                <InputNumber
                                    disabled={isViewOnly}
                                    style={{ width: '100%' }}
                                    precision={2}
                                    decimalSeparator=','
                                    min={0}
                                    onChange={(value: number | null) => {
                                        onChange({ ...userSettings, cashTransferCommission: value });
                                    }}
                                />
                            </Form.Item>
                            <Space.Addon>%</Space.Addon>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item
                        labelAlign='left'
                        label='Отключить автоматическую оплату счета'
                        initialValue={userSettings?.disableAutomaticBillPayment}
                        name='disableAutomaticBillPayment'
                        labelCol={{ xs: 22 }}
                        wrapperCol={{ xs: 2 }}
                    >
                        <Switch
                            size='small'
                            style={{ float: 'right' }}
                            disabled={isViewOnly}
                            onChange={(value) => {
                                onChange({ ...userSettings, disableAutomaticBillPayment: value });
                            }}
                        />
                    </Form.Item>
                </>
            )}

            <Form.Item wrapperCol={{ span: 24 }}>
                <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                    Разрешения
                </Divider>
            </Form.Item>
            <Form.Item
                labelAlign='left'
                key='showOrders'
                label='Заказы'
                initialValue={userSettings?.showOrders}
                valuePropName='checked'
                name='showOrders'
                style={{ marginBottom: 0 }}
                labelCol={{ xs: 22 }}
                wrapperCol={{ xs: 2 }}
            >
                <Switch
                    size='small'
                    style={{ float: 'right' }}
                    disabled={isViewOnly /*  || userType == UserType.SubClient */}
                    onChange={(value) => {
                        onChange({ ...userSettings, showOrders: value });
                    }}
                />
            </Form.Item>
            <Form.Item
                labelAlign='left'
                key='showBills'
                label='Счета'
                initialValue={userSettings?.showBills}
                valuePropName='checked'
                name='showBills'
                style={{ marginBottom: 0 }}
                labelCol={{ xs: 22 }}
                wrapperCol={{ xs: 2 }}
            >
                <Switch
                    size='small'
                    style={{ float: 'right' }}
                    disabled={isViewOnly || userType == UserType.SubClient}
                    onChange={(value) => {
                        onChange({ ...userSettings, showBills: value });
                    }}
                />
            </Form.Item>
            <Form.Item
                labelAlign='left'
                key='showBalances'
                label='Балансы'
                initialValue={userSettings?.showBalances}
                valuePropName='checked'
                name='showBalances'
                style={{ marginBottom: 0 }}
                labelCol={{ xs: 22 }}
                wrapperCol={{ xs: 2 }}
            >
                <Switch
                    size='small'
                    style={{ float: 'right' }}
                    disabled={isViewOnly || userType == UserType.SubClient}
                    onChange={(value) => {
                        onChange({ ...userSettings, showBalances: value });
                    }}
                />
            </Form.Item>
            <Form.Item
                labelAlign='left'
                key='showInventory'
                label='Складские остатки'
                initialValue={userSettings?.showInventory}
                valuePropName='checked'
                name='showInventory'
                style={{ marginBottom: 0 }}
                labelCol={{ xs: 22 }}
                wrapperCol={{ xs: 2 }}
            >
                <Switch
                    size='small'
                    style={{ float: 'right' }}
                    disabled={isViewOnly || userType == UserType.SubClient}
                    onChange={(value) => {
                        onChange({ ...userSettings, showInventory: value });
                    }}
                />
            </Form.Item>
            <Form.Item
                labelAlign='left'
                key='showTransactions'
                label='История операций'
                initialValue={userSettings?.showTransactions}
                valuePropName='checked'
                name='showTransactions'
                style={{ marginBottom: 0 }}
                labelCol={{ xs: 22 }}
                wrapperCol={{ xs: 2 }}
            >
                <Switch
                    size='small'
                    style={{ float: 'right' }}
                    disabled={isViewOnly || userType == UserType.SubClient}
                    onChange={(value) => {
                        onChange({ ...userSettings, showTransactions: value });
                    }}
                />
            </Form.Item>
            <Form.Item
                labelAlign='left'
                key='showDocuments'
                label='Документы'
                initialValue={userSettings?.showDocuments}
                valuePropName='checked'
                name='showDocuments'
                style={{ marginBottom: 0 }}
                labelCol={{ xs: 22 }}
                wrapperCol={{ xs: 2 }}
            >
                <Switch
                    size='small'
                    style={{ float: 'right' }}
                    disabled={isViewOnly || userType == UserType.SubClient}
                    onChange={(value) => {
                        onChange({ ...userSettings, showDocuments: value });
                    }}
                />
            </Form.Item>
        </>
    );
};

export default UserSettingsForm;
